import axios from 'axios';
import { useQuery } from 'react-query';
import Header from '../../../shared/Header';
import { Box, Stack } from '../../../shared/layouts';
import Placeholder from '../../../shared/Placeholder';
import Loader from '../../../shared/Loader';
import Labeled from '../../../shared/WithLabel';
import { useContext, useState } from 'react';
import { useNavigate, useParams} from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from '@dnhb/node_package';
import BackButton from '../../../shared/BackButton';
import ConfirmIcon from 'imagesUwKluis/svg/confirmed.svg';
import Card from '../../../shared/Card';
import Button from '../../../shared/Button';
import CheckBox from '../../../shared/CheckBox';
import DisplayLine from '../../../shared/DisplayLine';
import { formatISODate } from '../../../../utils/dateHelpers';
import MessagebarContext from '../../../../context/MessagebarContext';
import { getSigningStatusOptions } from '../../../../utils/constants';
import Input from '../../../shared/Input';
import {useTranslation} from "react-i18next";
import useIconLibrary from "../../../../hooks/useIconLibrary";

const SignRequestDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { breakpoints } = useTheme();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? '56.25rem'})`
  );
  const { setSuccesMessage, setAlertMessage, setCustomMessage } = useContext(MessagebarContext);
  const [accepted, setAccepted] = useState(false);
  const [signedBy, setSignedBy] = useState('');
  const [errors, setErrors] = useState({});
  const [signersCount, setSignersCount] = useState(0);

  const { DocumentsIcon } = useIconLibrary();

  const {
    isLoading,
    isError,
    data: {
      name,
      description,
      sign_status,
      sign_type,
      auth_method,
      assigned_person_name,
      created_at,
      sign_url,
      signed_at,
      signed_by,
      sign_request_signers,
    } = {},
  } = useQuery(['SignRequestItem', documentId], async () => {
    const { data } = await axios.get(`/sign-request/${documentId}`);
    setSignersCount(data.sign_request_signers.length);

    return data;
  });

  const submit = () => {
    setErrors({});
    if (!accepted) {
      setErrors({ accepted: [t('Dit veld is verplicht')] });
    } else {
      axios
        .put(`/sign-request/${documentId}/sign`, { signed_by: signedBy })
        .then(() => {
          setSuccesMessage(t('Het document is succesvol ondertekend.'));
          navigate('/requests');
        })
        .catch((err) => {
          setAlertMessage();
          setErrors(err.response?.data?.errors);
        });
    }
  };

  const iframeContent =
    sign_type === 'sign_request' && sign_status === 'open' ? sign_url : (
      signersCount === 1 &&
      sign_type === 'stiply' &&
      sign_status === 'open' &&
      auth_method !== 'idin' &&
      auth_method !== 'pimid'
        ? sign_request_signers[0].sign_url
        : `/sign-request/${documentId}/show.pdf`
    );

  return (
    <>
      {isLoading && <Loader />}
      <Stack space="xl" width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}>
        {documentId !== null && (
          <BackButton onClick={() => navigate('/requests')}>{t('Terug')}</BackButton>
        )}
        <Header
          title={t('Digitaal ondertekenen: {{name}}', { name: name ?? '' })}
          icon={<DocumentsIcon height="2rem" width="2rem" />}
          variant="page"
          button={
            <Button
              variant={'primary-outline'}
              onClick={() => {
                window.location.href = `/sign-request/${documentId}/download`;
                setCustomMessage(
                  t('De download is gestart. Een moment geduld.')
                );
              }}
            >
              {t('Download document')}
            </Button>
          }
        />
        {isError ? (
          <Placeholder>
            {t('Er is iets mis gegaan met het ophalen van dit tekenverzoek.')}
          </Placeholder>
        ) : (
          <Stack space="m">
            <Card>
              <Stack space={isMinDesktop ? 'm' : 's'}>
                <DisplayLine label={t('Beschrijving')} value={description} />
                <DisplayLine label={t('Te tekenen door')} value={assigned_person_name} />
                <DisplayLine
                  label={t('Ontvangen op')}
                  value={formatISODate(created_at)}
                />
                <DisplayLine
                  label={t('Status')}
                  value={getSigningStatusOptions()[sign_status]}
                />
                {sign_status === 'signed' && (
                  <>
                    <DisplayLine
                      label={t('Ondertekend door')}
                      value={signed_by}
                    />
                    <DisplayLine
                      label={t('Ondertekend op')}
                      value={formatISODate(signed_at)}
                    />
                  </>
                )}

                {isMinDesktop && (
                  <Box
                    css={{
                      position: 'relative',
                      overflow: 'hidden',
                      width: '100%',
                      paddingTop: '140%',
                    }}
                  >
                    {!isLoading && (
                      <iframe
                        src={iframeContent}
                        height="100%"
                        width="100%"
                        css={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                        }}
                      />
                    )}
                  </Box>
                )}
              </Stack>
            </Card>
            {sign_status === 'open' &&
              sign_type === 'stiply' &&
              (auth_method === 'idin' ||
                auth_method === 'pimid' ||
                signersCount > 1 ||
                !isMinDesktop) && (
                <Card>
                  <Header
                    title={t('Bij akkoord')}
                    icon={<ConfirmIcon height="2rem" width="2rem" />}
                    variant="section"
                  />
                  {sign_request_signers.map((signer) => (
                    <>
                      {signer.sign_status === 'open' && (
                        <Stack key={signer.id} space={'s'} mt={'m'}>
                          <Box mt={'m'}>
                            <p css={{ margin: '0 0 12px' }}>
                              {(signer.name ?? '') + ' (' + signer.email + ')'}
                            </p>
                            <Button
                              onClick={() => {
                                if (
                                  !isMinDesktop &&
                                  auth_method !== 'idin' &&
                                  auth_method !== 'pimid' &&
                                  signersCount === 1
                                ) {
                                  window.open(signer.sign_url, '_blank');
                                } else {
                                  window.location = signer.sign_url;
                                }
                              }}
                            >
                              {t('Ondertekenen')}
                            </Button>
                          </Box>
                        </Stack>
                      )}
                    </>
                  ))}
                </Card>
              )}
            {sign_status === 'open' && sign_type === 'checkbox' && (
              <Card>
                <Header
                  title={t('Bij akkoord')}
                  icon={<ConfirmIcon height="2rem" width="2rem" />}
                  variant="section"
                />
                <Stack space={'s'} mt={'m'}>
                  <Labeled label={t('Naam van ondertekenaar')}>
                    <Input
                      name={'signed_by'}
                      value={signedBy}
                      onChange={({ target }) => setSignedBy(target.value)}
                      errors={errors?.signed_by}
                    />
                  </Labeled>
                  <CheckBox
                    title={t('Ik ga akkoord met het bovenstaande document')}
                    name="accepted"
                    value={accepted}
                    onChange={({ target }) => setAccepted(target.value)}
                    errors={errors?.accepted}
                  />
                  <Box mt={'m'}>
                    <Button onClick={() => submit()}>{t('Ondertekenen')}</Button>
                  </Box>
                </Stack>
              </Card>
            )}
            {sign_status === 'open' && sign_type === 'sign_request' && !isMinDesktop && (
              <Card>
                <Header
                  title={t('Bij akkoord')}
                  icon={<ConfirmIcon height="2rem" width="2rem" />}
                  variant="section"
                />
                <Stack space={'s'} mt={'m'}>
                    <Button
                      onClick={() => window.open(sign_url, '_blank')}
                    >
                      {t('Ondertekenen')}
                    </Button>
                </Stack>
              </Card>
            )}
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default SignRequestDetailPage;
